export type PrintProviderMode = 'fake' | 'whcc_sandbox' | 'whcc_production'
export type PrintSubmissionMode = 'hold_for_review' | 'auto_submit'

export type PrintShipFromAddress = {
  name: string
  addr1: string
  addr2: string
  city: string
  state: string
  zip: string
  country: string
  phone: string
}

export type PrintProduct = {
  id: string
  name: string
  sizeLabel: string
  finish: string
  description: string
  priceCents: number
  currency: string
  active: boolean
  maxQuantity: number
  whcc: {
    productUid: number | null
    productNodeId: number | null
    attributeUids: number[]
    orderAttributeUids: number[]
  }
}

export type PrintFitStatus =
  | {
      kind: 'fits'
      message: string
      photoAspectRatio: number
      productAspectRatio: number
    }
  | {
      kind: 'crop_needed'
      message: string
      photoAspectRatio: number
      productAspectRatio: number
    }
  | {
      kind: 'unknown'
      message: string
      photoAspectRatio: null
      productAspectRatio: null
    }

export type PrintFulfillmentSettings = {
  enabled: boolean
  provider: 'whcc'
  providerMode: PrintProviderMode
  submissionMode: PrintSubmissionMode
  allowedCountries: string[]
  shippingLabel: string
  shippingRateCents: number
  stripeAutomaticTax: boolean
  shipFromAddress: PrintShipFromAddress
  catalog: PrintProduct[]
}

const DEFAULT_SHIP_FROM_ADDRESS: PrintShipFromAddress = {
  name: 'Jessica Deen Photography',
  addr1: '',
  addr2: '',
  city: '',
  state: 'CA',
  zip: '',
  country: 'US',
  phone: '',
}

export const DEFAULT_PRINT_PRODUCTS: PrintProduct[] = [
  {
    id: 'whcc-4x6-lustre',
    name: '4x6 Lustre Print',
    sizeLabel: '4 x 6',
    finish: 'Lustre',
    description: 'Archival photographic print fulfilled through WHCC.',
    priceCents: 600,
    currency: 'usd',
    active: false,
    maxQuantity: 25,
    whcc: {
      productUid: null,
      productNodeId: null,
      attributeUids: [],
      orderAttributeUids: [],
    },
  },
  {
    id: 'whcc-8x12-lustre',
    name: '8x12 Lustre Print',
    sizeLabel: '8 x 12',
    finish: 'Lustre',
    description: 'A clean sports-action print size that preserves the camera aspect ratio.',
    priceCents: 1800,
    currency: 'usd',
    active: false,
    maxQuantity: 20,
    whcc: {
      productUid: null,
      productNodeId: null,
      attributeUids: [],
      orderAttributeUids: [],
    },
  },
  {
    id: 'whcc-12x18-lustre',
    name: '12x18 Lustre Print',
    sizeLabel: '12 x 18',
    finish: 'Lustre',
    description: 'Large archival photographic print for display.',
    priceCents: 3600,
    currency: 'usd',
    active: false,
    maxQuantity: 10,
    whcc: {
      productUid: null,
      productNodeId: null,
      attributeUids: [],
      orderAttributeUids: [],
    },
  },
]

export const DEFAULT_PRINT_SETTINGS: PrintFulfillmentSettings = {
  enabled: false,
  provider: 'whcc',
  providerMode: 'whcc_sandbox',
  submissionMode: 'hold_for_review',
  allowedCountries: ['US'],
  shippingLabel: 'Standard shipping',
  shippingRateCents: 795,
  stripeAutomaticTax: false,
  shipFromAddress: DEFAULT_SHIP_FROM_ADDRESS,
  catalog: DEFAULT_PRINT_PRODUCTS,
}

export function normalizePrintSettings(value: unknown): PrintFulfillmentSettings {
  const candidate = value && typeof value === 'object'
    ? value as Partial<PrintFulfillmentSettings>
    : {}

  return {
    enabled: candidate.enabled ?? DEFAULT_PRINT_SETTINGS.enabled,
    provider: 'whcc',
    providerMode: normalizeProviderMode(candidate.providerMode),
    submissionMode: normalizeSubmissionMode(candidate.submissionMode),
    allowedCountries: normalizeAllowedCountries(candidate.allowedCountries),
    shippingLabel: normalizeText(candidate.shippingLabel, DEFAULT_PRINT_SETTINGS.shippingLabel),
    shippingRateCents: normalizeCents(candidate.shippingRateCents, DEFAULT_PRINT_SETTINGS.shippingRateCents),
    stripeAutomaticTax: candidate.stripeAutomaticTax ?? DEFAULT_PRINT_SETTINGS.stripeAutomaticTax,
    shipFromAddress: normalizeShipFromAddress(candidate.shipFromAddress),
    catalog: normalizePrintCatalog(candidate.catalog),
  }
}

export function getActivePrintProducts(settings: Pick<PrintFulfillmentSettings, 'enabled' | 'catalog'> | undefined) {
  if (!settings?.enabled) return []
  return settings.catalog.filter(product => product.active && product.priceCents > 0)
}

export function getDefaultPrintProduct(settings: Pick<PrintFulfillmentSettings, 'enabled' | 'catalog'> | undefined) {
  return getActivePrintProducts(settings)[0] ?? null
}

export function findActivePrintProduct(
  settings: Pick<PrintFulfillmentSettings, 'enabled' | 'catalog'> | undefined,
  productId: string
) {
  return getActivePrintProducts(settings).find(product => product.id === productId) ?? null
}

export function findPrintProductById(
  settings: Pick<PrintFulfillmentSettings, 'catalog'> | undefined,
  productId: string
) {
  return settings?.catalog.find(product => product.id === productId) ?? null
}

export function hasWhccProductMapping(product: PrintProduct) {
  return Number.isInteger(product.whcc.productUid)
    && Number.isInteger(product.whcc.productNodeId)
    && product.whcc.attributeUids.length > 0
}

export function formatPrintProductLabel(product: PrintProduct) {
  const parts = [product.sizeLabel, product.finish].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : product.name
}

export function getPrintFitStatus(
  photo: { width?: number | null; height?: number | null },
  product: Pick<PrintProduct, 'sizeLabel'>
): PrintFitStatus {
  const photoAspectRatio = normalizeAspectRatio(photo.width, photo.height)
  const productAspectRatio = parsePrintAspectRatio(product.sizeLabel)

  if (!photoAspectRatio || !productAspectRatio) {
    return {
      kind: 'unknown',
      message: 'Fit could not be verified automatically. Jessica will review before production.',
      photoAspectRatio: null,
      productAspectRatio: null,
    }
  }

  const difference = Math.abs(photoAspectRatio - productAspectRatio) / productAspectRatio
  if (difference <= 0.025) {
    return {
      kind: 'fits',
      message: 'Matches the photo aspect ratio; no crop expected.',
      photoAspectRatio,
      productAspectRatio,
    }
  }

  return {
    kind: 'crop_needed',
    message: 'Needs cropping for this size. Hidden until crop previews are added.',
    photoAspectRatio,
    productAspectRatio,
  }
}

export function isPrintProductSafeForPhoto(
  photo: { width?: number | null; height?: number | null },
  product: Pick<PrintProduct, 'sizeLabel'>
) {
  return getPrintFitStatus(photo, product).kind !== 'crop_needed'
}

function normalizePrintCatalog(value: unknown): PrintProduct[] {
  const rawProducts = Array.isArray(value) ? value : []
  const byId = new Map(DEFAULT_PRINT_PRODUCTS.map(product => [product.id, product]))
  const normalized = rawProducts
    .map((product, index) => normalizePrintProduct(product, index, byId))
    .filter((product): product is PrintProduct => Boolean(product))

  return normalized.length > 0 ? normalized : DEFAULT_PRINT_PRODUCTS
}

function parsePrintAspectRatio(sizeLabel: string) {
  const match = sizeLabel.match(/(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)/i)
  if (!match) return null

  return normalizeAspectRatio(Number(match[1]), Number(match[2]))
}

function normalizeAspectRatio(width: unknown, height: unknown) {
  if (typeof width !== 'number' || typeof height !== 'number') return null
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null

  const longSide = Math.max(width, height)
  const shortSide = Math.min(width, height)
  return longSide / shortSide
}

function normalizePrintProduct(value: unknown, index: number, defaults: Map<string, PrintProduct>) {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<PrintProduct>
  const fallback = defaults.get(String(candidate.id || '')) ?? DEFAULT_PRINT_PRODUCTS[index] ?? {
    id: `print-${index + 1}`,
    name: `Print ${index + 1}`,
    sizeLabel: '',
    finish: 'Lustre',
    description: '',
    priceCents: 0,
    currency: 'usd',
    active: false,
    maxQuantity: 10,
    whcc: {
      productUid: null,
      productNodeId: null,
      attributeUids: [],
      orderAttributeUids: [],
    },
  }

  const id = normalizeProductId(candidate.id, fallback.id)
  return {
    id,
    name: normalizeText(candidate.name, fallback.name),
    sizeLabel: normalizeText(candidate.sizeLabel, fallback.sizeLabel),
    finish: normalizeText(candidate.finish, fallback.finish),
    description: normalizeOptionalText(candidate.description, fallback.description),
    priceCents: normalizeCents(candidate.priceCents, fallback.priceCents),
    currency: normalizeCurrency(candidate.currency, fallback.currency),
    active: candidate.active ?? fallback.active,
    maxQuantity: normalizeQuantity(candidate.maxQuantity, fallback.maxQuantity),
    whcc: normalizeWhccMapping(candidate.whcc, fallback.whcc),
  }
}

function normalizeWhccMapping(value: unknown, fallback: PrintProduct['whcc']): PrintProduct['whcc'] {
  const candidate = value && typeof value === 'object'
    ? value as Partial<PrintProduct['whcc']>
    : {}

  return {
    productUid: normalizeNullableInteger(candidate.productUid, fallback.productUid),
    productNodeId: normalizeNullableInteger(candidate.productNodeId, fallback.productNodeId),
    attributeUids: normalizeIntegerList(candidate.attributeUids, fallback.attributeUids),
    orderAttributeUids: normalizeIntegerList(candidate.orderAttributeUids, fallback.orderAttributeUids),
  }
}

function normalizeShipFromAddress(value: unknown): PrintShipFromAddress {
  const candidate = value && typeof value === 'object'
    ? value as Partial<PrintShipFromAddress>
    : {}

  return {
    name: normalizeText(candidate.name, DEFAULT_SHIP_FROM_ADDRESS.name),
    addr1: normalizeOptionalText(candidate.addr1, DEFAULT_SHIP_FROM_ADDRESS.addr1),
    addr2: normalizeOptionalText(candidate.addr2, DEFAULT_SHIP_FROM_ADDRESS.addr2),
    city: normalizeOptionalText(candidate.city, DEFAULT_SHIP_FROM_ADDRESS.city),
    state: normalizeOptionalText(candidate.state, DEFAULT_SHIP_FROM_ADDRESS.state).toUpperCase(),
    zip: normalizeOptionalText(candidate.zip, DEFAULT_SHIP_FROM_ADDRESS.zip),
    country: normalizeCountry(candidate.country, DEFAULT_SHIP_FROM_ADDRESS.country),
    phone: normalizeOptionalText(candidate.phone, DEFAULT_SHIP_FROM_ADDRESS.phone),
  }
}

function normalizeProviderMode(value: unknown): PrintProviderMode {
  return value === 'fake' || value === 'whcc_sandbox' || value === 'whcc_production'
    ? value
    : DEFAULT_PRINT_SETTINGS.providerMode
}

function normalizeSubmissionMode(value: unknown): PrintSubmissionMode {
  return value === 'auto_submit' || value === 'hold_for_review'
    ? value
    : DEFAULT_PRINT_SETTINGS.submissionMode
}

function normalizeAllowedCountries(value: unknown) {
  if (!Array.isArray(value)) return DEFAULT_PRINT_SETTINGS.allowedCountries
  const countries = value
    .map(country => normalizeCountry(country, ''))
    .filter(Boolean)

  return countries.length > 0 ? Array.from(new Set(countries)) : DEFAULT_PRINT_SETTINGS.allowedCountries
}

function normalizeCountry(value: unknown, fallback: string) {
  if (typeof value !== 'string') return fallback
  const normalized = value.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(normalized) ? normalized : fallback
}

function normalizeText(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function normalizeOptionalText(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback
}

function normalizeProductId(value: unknown, fallback: string) {
  if (typeof value !== 'string') return fallback
  const normalized = value.trim().toLowerCase()
  return /^[a-z0-9][a-z0-9_-]{1,63}$/.test(normalized) ? normalized : fallback
}

function normalizeCurrency(value: unknown, fallback: string) {
  if (typeof value !== 'string') return fallback
  const normalized = value.trim().toLowerCase()
  return /^[a-z]{3}$/.test(normalized) ? normalized : fallback
}

function normalizeCents(value: unknown, fallback: number) {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric) || numeric < 0) return fallback
  return Math.round(numeric)
}

function normalizeQuantity(value: unknown, fallback: number) {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isInteger(numeric) || numeric < 1 || numeric > 100) return fallback
  return numeric
}

function normalizeNullableInteger(value: unknown, fallback: number | null) {
  if (value === null || value === '') return null
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isInteger(numeric) && numeric > 0 ? numeric : fallback
}

function normalizeIntegerList(value: unknown, fallback: number[]) {
  if (!Array.isArray(value)) return fallback
  const values = value
    .map(item => typeof item === 'number' ? item : Number(item))
    .filter(item => Number.isInteger(item) && item > 0)

  return Array.from(new Set(values))
}
